import { type ComponentProps, FC, type JSX } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { NotificationBox, Stack } from '@eneco/sparky';

import type { PriceDetailsOverviewTextualData } from './price-details-overview.types';
import { usePriceDetailsOverview } from './usePriceDetailsOverview';
import { FlowConsumerContextEnergy } from '../../../context';
import { PriceDetails } from '../../../shared/components';
import { PriceDetailsOverviewRedeliveryNotification } from '../../../shared/components/PriceDetailsOverviewRedeliveryNotification/PriceDetailsOverviewRedeliveryNotification';
import { getCashbackTexts, getPriceDetailsExplanationTexts } from '../../../shared/helpers';
import { ENECO_ENERGY_FLOW_GIFT } from '../../../shared/helpers/constants';
import { DetailsType, type CostDetails, type Offer } from '../../../shared/types';

interface Props {
  flowContext: FlowConsumerContextEnergy;
  offer?: Offer;
  textualData?: PriceDetailsOverviewTextualData;

  costDetails?: CostDetails;
}

const ProductContent: FC<ComponentProps<typeof PriceDetails.Content>> = ({ summary, details }) => (
  <PriceDetails.Content summary={summary} details={details} />
);

const ProductFooter: FC<ComponentProps<typeof PriceDetails.Footer>> = ({ summary, details }) => (
  <PriceDetails.Footer summary={summary} details={details} />
);

export const PriceDetailsOverview: FC<Props> = ({ flowContext, textualData, offer }): JSX.Element => {
  const {
    costDetails,
    usages,
    electricitySection,
    gasSection,
    warmthSection,
    tapWaterSection,
    warmthExchangeSetSection,
    geothermalHeatingSection,
    shouldShowDynamicContractNotification,
    shouldShowHybridContractNotification,
    shouldShowVariablePricingNotification,
  } = usePriceDetailsOverview({ flowContext, offer, textualData });

  return (
    <Stack direction="column" gap="4">
      <PriceDetailsOverviewRedeliveryNotification
        flowContext={flowContext}
        usageData={usages}
        redeliveryMoreNotificationTextualData={textualData?.electricityCostDetails?.redeliveryMoreNotification}
        redeliveryLessNotificationTextualData={textualData?.electricityCostDetails?.redeliveryLessNotification}
      />

      {shouldShowDynamicContractNotification && (
        <NotificationBox
          isAlert={false}
          variant={textualData?.costDetails?.dynamicPricingNotification?.value?.variant}
          title={textualData?.costDetails?.dynamicPricingNotification?.value?.title}
          text={<RichText html={textualData?.costDetails?.dynamicPricingNotification?.value?.content} />}
        />
      )}

      {shouldShowHybridContractNotification && (
        <NotificationBox
          isAlert={false}
          variant={textualData?.costDetails?.hybridNotification?.value?.variant}
          title={textualData?.costDetails?.hybridNotification?.value?.title}
          text={<RichText html={textualData?.costDetails?.hybridNotification?.value?.content} />}
        />
      )}

      {shouldShowVariablePricingNotification && (
        <NotificationBox
          isAlert={false}
          variant={textualData?.costDetails?.variableProductNotification?.value?.variant}
          title={textualData?.costDetails?.variableProductNotification?.value?.title}
          text={<RichText html={textualData?.costDetails?.variableProductNotification?.value?.content} />}
        />
      )}

      {costDetails?.cashback && costDetails?.cashback?.type !== ENECO_ENERGY_FLOW_GIFT && (
        <PriceDetails>
          <PriceDetails.Title type={DetailsType.Cashback}>
            {textualData?.productTypeLabels?.[
              costDetails?.cashback?.type === 'cashBackDirect' ? 'cashbackDirectLabel' : 'cashbackOnYearNoteLabel'
            ]?.value ?? ''}
          </PriceDetails.Title>

          <PriceDetails.Content
            summary={{
              text: getCashbackTexts(costDetails?.cashback?.type, textualData?.promotionTexts)?.label ?? '',
              value: (costDetails?.cashback?.value ?? 0) * -1,
            }}
          />

          <PriceDetails.Footer
            summary={{
              label: textualData?.costDetails?.totalCostsLabel?.value,
              value: (costDetails?.cashback?.value ?? 0) * -1,
            }}
          />
        </PriceDetails>
      )}

      {electricitySection && (
        <PriceDetails>
          <PriceDetails.Title type={DetailsType.Electricity}>{electricitySection.title}</PriceDetails.Title>
          <ProductContent {...electricitySection.variableCosts} />
          {electricitySection.governmentLevies && <ProductContent {...electricitySection.governmentLevies} />}
          <ProductContent {...electricitySection.fixedCosts} />
          {electricitySection.redelivery && <ProductContent {...electricitySection.redelivery} />}
          <ProductFooter {...electricitySection.footer} />
        </PriceDetails>
      )}

      {gasSection && (
        <PriceDetails>
          <PriceDetails.Title type={DetailsType.Gas}>{gasSection.title}</PriceDetails.Title>
          <ProductContent {...gasSection.variableCosts} />
          {gasSection.governmentLevies && <ProductContent {...gasSection.governmentLevies} />}
          <ProductContent {...gasSection.fixedCosts} />
          <ProductFooter {...gasSection.footer} />
        </PriceDetails>
      )}

      {warmthSection && (
        <PriceDetails>
          <PriceDetails.Title type={DetailsType.Warmth}>{warmthSection.title}</PriceDetails.Title>
          <ProductContent {...warmthSection.variableCosts} />
          <ProductContent {...warmthSection.fixedCosts} />
          <ProductFooter {...warmthSection.footer} />
        </PriceDetails>
      )}

      {tapWaterSection && (
        <PriceDetails>
          <PriceDetails.Title type={DetailsType.Water}>{tapWaterSection.title}</PriceDetails.Title>
          <ProductContent {...tapWaterSection.variableCosts} />
          <ProductFooter {...tapWaterSection.footer} />
        </PriceDetails>
      )}

      {warmthExchangeSetSection && (
        <PriceDetails>
          <PriceDetails.Title type={DetailsType.Warmth}>{warmthExchangeSetSection.title}</PriceDetails.Title>
          {warmthExchangeSetSection.contents.map(content => (
            <ProductContent key={content.summary?.text} {...content} />
          ))}
          <ProductFooter {...warmthExchangeSetSection.footer} />
        </PriceDetails>
      )}

      {geothermalHeatingSection && (
        <PriceDetails>
          <PriceDetails.Title type={DetailsType.Warmth}>{geothermalHeatingSection.title}</PriceDetails.Title>
          {geothermalHeatingSection.contents.map(content => (
            <ProductContent key={content.summary?.text} {...content} />
          ))}
          <ProductFooter {...geothermalHeatingSection.footer} />
        </PriceDetails>
      )}

      <PriceDetails>
        <PriceDetails.Content
          summary={{ text: textualData?.costDetailsExplanation?.explanationTitle?.value, highlightText: true }}
          text={getPriceDetailsExplanationTexts(flowContext, offer, textualData?.costDetailsExplanation)}
        />
      </PriceDetails>
    </Stack>
  );
};
